/**
 * FA phases → phased-engine inputs (Phase-2 item 2.5, defect B10).
 *
 * ONE adapter, so every surface that wants per-phase economics gets them from
 * the SAME kernel (`projectFinancePhased`) instead of re-deriving its own.
 *
 * The phased engine is INTENT-shaped: it takes a `ProjectIntent` (whose
 * `phases[].capacityMW` is the CUMULATIVE site capacity at that phase's
 * completion) plus a `PhaseCapexSourceMap` keyed by `ProjectPhase.id` carrying
 * the per-phase CAPEX / capacity-factor / O&M / COD overrides. The financial
 * model is FA-shaped (`FinancialAssumptions.phases[]`, same cumulative
 * convention, linked to the intent by `FinancialPhase.phaseId ===
 * ProjectPhase.id`, falling back to `sequence` for models authored before the
 * ids were linked).
 *
 * This module does that translation and nothing else — no economics. It
 * deliberately mirrors the linking rule in
 * `buildFinancialAnalysis.buildPhaseCapexSourceFromPlan` (`faById` → `faBySequence`)
 * so a surface fed from the FA alone and the report fed from the BoM agree
 * about WHICH financial phase describes WHICH intent phase.
 *
 * NOT modelled here: the BoM-derived AUTHORITATIVE per-phase CAPEX and the A2
 * cost-timing drawdown. Those need a `SupplyChainPlan`, which only the report
 * path (`buildFinancialAnalysis`) resolves. Surfaces using this adapter
 * therefore price a phase at the CAPEX the user entered on it, and unpriced
 * phases fall back to the engine's own capacity-fraction split.
 *
 * Unit discipline is inherited unchanged: MW, USD, percent (NOT fraction).
 */
import { effectivePhases } from './projectIntent';
import type { PhaseCapexSourceMap } from './projectFinancePhased';
import type { FinancialPhase } from '../types/financialAssumptions';
import { sortFinancialPhases } from '../types/financialAssumptions';
import type { ProjectIntent, ProjectPhase } from '../types/siteRag';

/**
 * Link an intent phase to the financial phase that describes it: by id first
 * (the linked case), then by `sequence` (legacy models). Same rule as
 * `buildFinancialAnalysis`.
 */
function financialPhaseFinder(
  financialPhases: readonly FinancialPhase[],
): (p: ProjectPhase) => FinancialPhase | undefined {
  const byId = new Map(financialPhases.map((fp) => [fp.phaseId, fp] as const));
  const bySequence = new Map(financialPhases.map((fp) => [fp.sequence, fp] as const));
  return (p: ProjectPhase) => byId.get(p.id) ?? bySequence.get(p.sequence);
}

/**
 * Build the engine's `PhaseCapexSourceMap` from the FA's per-phase overrides.
 *
 * Every field is null when the FA does not override it, so the engine falls
 * back to the campus value FIELD BY FIELD (a phase that sets only its capacity
 * factor still inherits campus O&M) — and a phase with no CAPEX of its own
 * falls back to the engine's capacity-fraction split of the project total.
 */
export function phaseCapexSourceFromFinancialPhases(
  intentPhases: readonly ProjectPhase[],
  financialPhases: readonly FinancialPhase[],
): PhaseCapexSourceMap {
  const faFor = financialPhaseFinder(financialPhases);
  const map: PhaseCapexSourceMap = {};
  for (const p of intentPhases) {
    const fp = faFor(p);
    map[p.id] = {
      capexTotalUsd: fp?.capexTotalUsd ?? null,
      bom: null,
      capacityFactorPct: fp?.capacityFactorPct ?? null,
      fixedOmPerKwYearUsd: fp?.fixedOmPerKwYearUsd ?? null,
      variableOmPerMwhUsd: fp?.variableOmPerMwhUsd ?? null,
      fuelCostPerMwhUsd: fp?.fuelCostPerMwhUsd ?? null,
      codIsoOverride: fp?.inServiceDate ?? null,
    };
  }
  return map;
}

/**
 * A minimal `ProjectIntent` whose phases mirror the FA's own — used when the
 * site carries no phased intent but the financial model IS phased (the FA form
 * can add phases without the Project Intent form having any).
 *
 * Only the fields `effectivePhases` reads are meaningful; the five required
 * categorical fields are set to 'undecided' and are never consulted by the
 * phased engine. Returns null when there are no financial phases to mirror.
 */
export function syntheticIntentFromFinancialPhases(
  financialPhases: readonly FinancialPhase[],
): ProjectIntent | null {
  if (financialPhases.length === 0) return null;
  const sorted = sortFinancialPhases([...financialPhases]);
  const phases: ProjectPhase[] = sorted.map((fp) => ({
    id: fp.phaseId,
    label: fp.label ?? `Phase ${fp.sequence}`,
    sequence: fp.sequence,
    // CUMULATIVE convention on both sides — copied straight across.
    capacityMW: fp.capacityMW,
    targetInServiceDate: fp.inServiceDate,
    powerSource: 'undecided',
  }));
  return {
    powerRequirement: 'undecided',
    coolingStrategy: 'undecided',
    nuclearInterest: 'undecided',
    timeline: 'undecided',
    priority: 'undecided',
    phases,
  };
}

/**
 * Display label per resolved phase id — the FA phase's label when it carries
 * one (that is what the user typed in the financial model), else the intent
 * phase's. Consumers render the engine's `perPhase[]` rows, which carry only
 * ids, so the label has to be resolved through the same linking rule.
 */
function labelByPhaseId(
  intentPhases: readonly ProjectPhase[],
  financialPhases: readonly FinancialPhase[],
): Record<string, string> {
  const faFor = financialPhaseFinder(financialPhases);
  const out: Record<string, string> = {};
  for (const p of intentPhases) {
    out[p.id] = faFor(p)?.label ?? p.label ?? `Phase ${p.sequence}`;
  }
  return out;
}

export interface PhasedEngineInputs {
  intent: ProjectIntent;
  source: PhaseCapexSourceMap;
  /** Display label per phase id — see `labelByPhaseId`. */
  labels: Record<string, string>;
  /**
   * 'intent' — the site's own phased Project Intent drives the phase set (what
   * the report engine uses, so the surface and the report agree).
   * 'financial' — the intent is not phased, so the phase set was mirrored from
   * `FinancialAssumptions.phases[]`.
   */
  phaseBasis: 'intent' | 'financial';
}

/**
 * Resolve the (intent, source-map) pair the phased engine needs from whatever
 * the caller has.
 *
 * Prefers the site's REAL project intent when it is phased — that is the phase
 * set `buildFinancialAnalysis` runs the report on, so a surface built on it
 * cannot disagree with the report. Falls back to a synthetic intent mirroring
 * the FA phases when the intent carries none.
 *
 * Returns null when neither side is phased (the caller should then use the
 * plain single-phase `projectFinance` path, which the phased kernel reproduces
 * exactly for one phase anyway).
 */
export function resolvePhasedEngineInputs(
  financialPhases: readonly FinancialPhase[] | undefined | null,
  intent?: ProjectIntent | null,
): PhasedEngineInputs | null {
  const faPhases = financialPhases ?? [];
  const intentView = effectivePhases(intent);
  if (intentView.isPhased && intent) {
    return {
      intent,
      source: phaseCapexSourceFromFinancialPhases(intentView.phases, faPhases),
      labels: labelByPhaseId(intentView.phases, faPhases),
      phaseBasis: 'intent',
    };
  }
  const synthetic = syntheticIntentFromFinancialPhases(faPhases);
  if (!synthetic) return null;
  const syntheticPhases = synthetic.phases ?? [];
  return {
    intent: synthetic,
    source: phaseCapexSourceFromFinancialPhases(syntheticPhases, faPhases),
    labels: labelByPhaseId(syntheticPhases, faPhases),
    phaseBasis: 'financial',
  };
}
